import * as React from 'react';
import { Col, Row, Popover, Button, Spin } from 'antd';
import { Prompt } from 'react-router';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';

import { FormatService } from 'src/core/services/format.service';
import { Column, DataTableView } from 'src/core/ui/data-table.view';
import { DateView } from 'src/core/ui/date.view';
import { CardController } from 'src/app/shared/card.controller';
import { HeaderView } from 'src/app/shared/header.view';
import { UserEditorController } from "src/app/general/users/parts/user-editor.controller";
import { TextView } from 'src/core/ui/text.view';

interface IApiKeyListViewProps extends ComponentViewProps<DataSet<ApiKeyItem>> {
	mode: ControlMode;
	onModeChange: (mode: ControlMode) => void;
	createNewItem: () => Promise<ApiKeyItem>;
	save: () => Promise<void>;
	cancel: () => Promise<void>;
	tokens: { id: number; token: string; }[];
	getToken: (id: number) => () => void;
}

@observer
export class ApiKeyListView extends React.Component<IApiKeyListViewProps, {}> {
	@resolve
	private format: FormatService;

	public render() {
		const { mode, model, onModeChange, save, cancel, createNewItem, setBusy } = this.props;

		if (model == null || model.items == null) {
			return null;
		}

		const columns: Column[] = [
			{
				title: 'User',
				key: 'userId',
				dataIndex: 'userUserName',
				editable: (value: string, record: ApiKeyItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						const { mode, setBusy } = this.props;
						return (
							<UserEditorController
								label={null}
								value={record.userId}
								mode={mode}
								onChanged={this.onUserChange(record)}
								required={true}
								setBusy={setBusy}
							/>
						);
					}

					return value || "";
				}
			},
			{
				title: 'Expiration',
				key: 'expirationDate',
				dataIndex: 'expirationDate',
				width: '200px',
				editable: (value: Date, record: ApiKeyItem, index: number, isSelected: boolean) => {
					if (isSelected) {
						const { mode } = this.props;
						return (
							<DateView
								dateUsage="utc"
								format="small-date"
								label={null}
								mode={mode}
								onChanged={this.onDateChange(record)}
								required={true}
								showTime={false}
								value={value}
							/>
						);
					}

					return this.format.toString("short-date", value, "utc");
				}
			},
			{
				title: '',
				key: 'cmd',
				dataIndex: 'id',
				width: '100px',
				render: (value: number) => {
					const { tokens, getToken } = this.props;
					const token = tokens.find(e => e.id === value);
					const content = (
						<React.Fragment>
							{token ? <TextView value={token.token} mode="edit" rows={5} /> : <Spin spinning={true} />}
							<Button onClick={this.copyToClipboard}>Copy to clipboard</Button>
						</React.Fragment>
					);
					return (
						<Popover
							placement="left"
							title="Token"
							content={content}
							trigger="click"
						>
							<Button disabled={value <= 0} icon="eye" onClick={token ? undefined : getToken(value)} />
						</Popover>
					);
				}
			},
		];

		return (
			<div className="window window-normal">
				<Prompt
					when={model.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24}>
						<HeaderView title="API Keys"
							mode={mode}
							onModeChanged={onModeChange}
							save={save}
							cancel={cancel}
							contextService={model}
						/>
					</ Col>
					<Col xs={24}>
						<CardController
							className="card-borderless"
							mode={mode}
						>
							<DataTableView
								model={model}
								columns={columns}
								setBusy={setBusy}
								mode={mode}
								createNewItem={createNewItem}
							/>
						</CardController>
					</Col>
				</Row>
			</div>
		);
	}

	private onUserChange = (record: ApiKeyItem) => (user: UserSummary) => {
		record.userId = user ? user.id : undefined;
	}

	private onDateChange = (record: ApiKeyItem) => (value: Date) => {
		record.expirationDate = value;
	}

	private copyToClipboard = (ev: React.MouseEvent<HTMLButtonElement>) => {
		ev.currentTarget.previousElementSibling.querySelector("textarea").select();
		document.execCommand("copy");
	}
}



// WEBPACK FOOTER //
// ./src/app/general/api-keys/api-key-list.view.tsx